/**
 * Parse a JavaScript stack trace into structured frames.
 *
 * Browsers disagree on the format:
 *   Chrome / Edge   "    at fnName (https://host/file.js:12:345)"
 *                   "    at https://host/file.js:12:345"
 *                   "    at async fnName (https://host/file.js:12:345)"
 *   Firefox/Safari  "fnName@https://host/file.js:12:345"
 *                   "@https://host/file.js:12:345"
 *
 * The URL itself contains colons ("https:"), so line and column are always
 * taken from the END of the location. Pure and dependency-free so it can run
 * in the report API, the admin API and tests alike.
 */
export type StackFrame = {
  /** Function name as it appears in the trace (minified in production). */
  fn: string | null
  /** Script URL or path. */
  file: string
  /** 1-based line. */
  line: number
  /** 1-based column, as browsers report it. */
  column: number
}

const CHROME_WITH_FN = /^\s*at\s+(?:async\s+)?(.*?)\s+\((.+?):(\d+):(\d+)\)\s*$/
const CHROME_NO_FN = /^\s*at\s+(?:async\s+)?(.+?):(\d+):(\d+)\s*$/
const GECKO = /^\s*(.*?)@(.+?):(\d+):(\d+)\s*$/

export function parseStack(stack: string | null | undefined, max = 30): StackFrame[] {
  if (!stack) return []
  const frames: StackFrame[] = []
  for (const raw of stack.split("\n")) {
    if (frames.length >= max) break
    let m = raw.match(CHROME_WITH_FN)
    if (m) {
      frames.push({ fn: cleanFn(m[1]), file: m[2], line: Number(m[3]), column: Number(m[4]) })
      continue
    }
    m = raw.match(CHROME_NO_FN)
    if (m) {
      frames.push({ fn: null, file: m[1], line: Number(m[2]), column: Number(m[3]) })
      continue
    }
    m = raw.match(GECKO)
    if (m && /[/:]/.test(m[2])) {
      frames.push({ fn: cleanFn(m[1]), file: m[2], line: Number(m[3]), column: Number(m[4]) })
    }
  }
  return frames
}

/** "Object.V [as internalMsgHandler]" -> "Object.V"; "" -> null. */
function cleanFn(fn: string | undefined): string | null {
  const s = (fn ?? "").replace(/\s*\[as [^\]]+\]\s*$/, "").trim()
  return s.length ? s : null
}

const EXTENSION_SCHEMES = /^(chrome-extension|moz-extension|safari-extension|safari-web-extension|ms-browser-extension|edge-extension):/i

export function isExtensionFrame(f: StackFrame): boolean {
  return EXTENSION_SCHEMES.test(f.file)
}

/** A Next.js client bundle file served by us. */
export function isOurBundleFrame(f: StackFrame): boolean {
  return /\/_next\/static\//.test(f.file) && !isExtensionFrame(f)
}

/**
 * The chunk path a frame points at, relative to `.next/static`, e.g.
 * "chunks/3i39qvf7bzc0m.js". Vercel may serve chunks under
 * "/_next/static/immutable/chunks/...", which maps to the same file.
 * Returns null for anything that is not a safe, plain chunk path.
 */
export function chunkPathOf(f: StackFrame): string | null {
  const m = f.file.match(/\/_next\/static\/(?:immutable\/)?(chunks\/[^?#]+?\.js)(?:[?#].*)?$/)
  if (!m) return null
  const p = m[1]
  if (p.includes("..") || !/^chunks\/[A-Za-z0-9_\-./[\]]+\.js$/.test(p)) return null
  return p
}
